import type * as z from 'zod';

import {
  ROUTES,
  ROUTE_KEYS,
  splitRequestInput,
  serialiseQuery,
  hasRequestBody,
  type AnyRoute,
  type RouteKey,
  type RouteManifest,
  type RequestInputRecord,
} from '../routes/index.js';
import { ApiError, apiErrorFromBody } from './api-error.js';
import {
  JSON_CONTENT_TYPE,
  NDJSON_CONTENT_TYPE,
  type FetchLike,
  type ResponseLike,
} from './transport.js';

/**
 * The typed client, generated FROM the route manifest.
 *
 * There is one implementation of "call an endpoint" and one of "stream an
 * endpoint"; every method on the client is the same closure bound to a different
 * manifest entry. Adding an endpoint to the manifest adds a client method with
 * no code change here — that is the whole point.
 */

export interface RequestOptions {
  readonly signal?: AbortSignal;
  readonly headers?: Readonly<Record<string, string>>;
  /** Override the client-wide setting for one call. */
  readonly validateResponse?: boolean;
}

export interface ApiClientOptions {
  /** Same-origin by default — the platform deploys web and API behind one host. */
  readonly baseUrl?: string;
  readonly fetch?: FetchLike;
  /** Access token lives in memory in the web app; the refresh token is a cookie. */
  readonly getAccessToken?: () => string | null | undefined;
  readonly defaultHeaders?: Readonly<Record<string, string>>;
  /**
   * Validate every response against its contract schema. Defaults to true: web
   * and API ship from the same package version, so a mismatch is a bug that
   * should fail loudly rather than corrupt the UI silently.
   */
  readonly validateResponses?: boolean;
  /** Called on any 401 so the app can trigger a refresh or bounce to login. */
  readonly onUnauthenticated?: (error: ApiError) => void;
}

interface ResolvedConfig {
  readonly baseUrl: string;
  readonly fetch: FetchLike;
  readonly getAccessToken: (() => string | null | undefined) | undefined;
  readonly defaultHeaders: Readonly<Record<string, string>>;
  readonly validateResponses: boolean;
  readonly onUnauthenticated: ((error: ApiError) => void) | undefined;
}

type OperationInput<TRoute extends AnyRoute> = z.input<TRoute['request']>;
type OperationOutput<TRoute extends AnyRoute> = z.output<TRoute['response']>;

/** An all-optional (or empty) input may be omitted entirely. */
type OperationArgs<TRoute extends AnyRoute> = Record<string, never> extends OperationInput<TRoute>
  ? [input?: OperationInput<TRoute>, options?: RequestOptions]
  : [input: OperationInput<TRoute>, options?: RequestOptions];

type OperationReturn<TRoute extends AnyRoute> = TRoute extends { readonly streaming: true }
  ? AsyncIterableIterator<OperationOutput<TRoute>>
  : Promise<OperationOutput<TRoute>>;

export type ApiOperations = {
  readonly [K in RouteKey]: (
    ...args: OperationArgs<RouteManifest[K]>
  ) => OperationReturn<RouteManifest[K]>;
};

export interface ApiClient extends ApiOperations {
  /** The manifest the client was built from, for callers that need route metadata. */
  readonly routes: RouteManifest;
  /** Escape hatch: call any route dynamically (used by generic UI and tests). */
  readonly call: (
    route: AnyRoute,
    input?: RequestInputRecord,
    options?: RequestOptions,
  ) => Promise<unknown>;
  readonly stream: (
    route: AnyRoute,
    input?: RequestInputRecord,
    options?: RequestOptions,
  ) => AsyncIterableIterator<unknown>;
}

const resolveConfig = (options: ApiClientOptions): ResolvedConfig => {
  // Bound to `globalThis`, not merely referenced. The resolved config holds this
  // function as a property and `send` calls it as `config.fetch(...)`, which makes
  // the config object the receiver — and a browser's `window.fetch` refuses any
  // receiver but the window: "Failed to execute 'fetch' on 'Window': Illegal
  // invocation". Node's undici ignores the receiver, which is why every unit test
  // passed while no browser request could ever have worked.
  const fetchImpl =
    options.fetch ?? (globalThis.fetch as FetchLike | undefined)?.bind(globalThis);
  if (fetchImpl === undefined) {
    throw new Error('createApiClient: no fetch implementation available');
  }
  return {
    baseUrl: (options.baseUrl ?? '').replace(/\/$/, ''),
    fetch: fetchImpl,
    getAccessToken: options.getAccessToken,
    defaultHeaders: options.defaultHeaders ?? {},
    validateResponses: options.validateResponses ?? true,
    onUnauthenticated: options.onUnauthenticated,
  };
};

const buildHeaders = (
  route: AnyRoute,
  config: ResolvedConfig,
  options: RequestOptions | undefined,
  sendsBody: boolean,
): Record<string, string> => {
  const headers: Record<string, string> = {
    accept: route.streaming === true ? NDJSON_CONTENT_TYPE : JSON_CONTENT_TYPE,
    ...config.defaultHeaders,
    ...options?.headers,
  };
  if (sendsBody) headers['content-type'] = JSON_CONTENT_TYPE;
  const token = config.getAccessToken?.();
  if (token !== undefined && token !== null && token.length > 0) {
    headers['authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const isAbort = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  (error as { name?: unknown }).name === 'AbortError';

const send = async (
  route: AnyRoute,
  input: RequestInputRecord,
  options: RequestOptions | undefined,
  config: ResolvedConfig,
): Promise<ResponseLike> => {
  const split = splitRequestInput(route, input);
  const sendsBody = hasRequestBody(route);
  const url = `${config.baseUrl}${split.path}${serialiseQuery(split.query)}`;

  try {
    return await config.fetch(url, {
      method: route.method,
      headers: buildHeaders(route, config, options, sendsBody),
      body: sendsBody ? JSON.stringify(split.body ?? {}) : undefined,
      // Refresh-token cookie is HttpOnly and same-origin; it must ride along.
      credentials: 'include',
      signal: options?.signal,
      cache: 'no-store',
    });
  } catch (error) {
    if (isAbort(error)) {
      throw new ApiError({
        code: 'REQUEST_ABORTED',
        message: `${route.operationId} was aborted`,
        cause: error,
      });
    }
    throw new ApiError({
      code: 'NETWORK',
      message: `${route.operationId} could not reach the API`,
      cause: error,
    });
  }
};

const failure = async (
  response: ResponseLike,
  config: ResolvedConfig,
): Promise<ApiError> => {
  const body = await response.text().catch(() => '');
  const error = apiErrorFromBody(response.status, body);
  if (error.code === 'UNAUTHENTICATED') config.onUnauthenticated?.(error);
  return error;
};

const parseWith = (
  route: AnyRoute,
  schema: z.ZodType,
  value: unknown,
  validate: boolean,
): unknown => {
  if (!validate) return value;
  const result = schema.safeParse(value);
  if (!result.success) {
    throw new ApiError({
      code: 'CONTRACT_VIOLATION',
      message: `${route.operationId} returned a response that does not match its contract`,
      details: result.error.issues,
    });
  }
  return result.data;
};

const callRoute = async (
  route: AnyRoute,
  input: RequestInputRecord,
  options: RequestOptions | undefined,
  config: ResolvedConfig,
): Promise<unknown> => {
  const response = await send(route, input, options, config);
  if (!response.ok) throw await failure(response, config);

  const text = await response.text();
  if (text.length === 0) return undefined;

  let payload: unknown;
  try {
    payload = JSON.parse(text) as unknown;
  } catch (error) {
    throw new ApiError({
      code: 'CONTRACT_VIOLATION',
      message: `${route.operationId} returned a body that is not JSON`,
      status: response.status,
      cause: error,
    });
  }

  return parseWith(
    route,
    route.response,
    payload,
    options?.validateResponse ?? config.validateResponses,
  );
};

async function* streamRoute(
  route: AnyRoute,
  input: RequestInputRecord,
  options: RequestOptions | undefined,
  config: ResolvedConfig,
): AsyncIterableIterator<unknown> {
  const response = await send(route, input, options, config);
  if (!response.ok) throw await failure(response, config);

  const body = response.body;
  if (body === null || body === undefined) {
    throw new ApiError({
      code: 'CONTRACT_VIOLATION',
      message: `${route.operationId} returned no stream body`,
      status: response.status,
    });
  }

  const validate = options?.validateResponse ?? config.validateResponses;
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const emit = (line: string): unknown => {
    let chunk: unknown;
    try {
      chunk = JSON.parse(line) as unknown;
    } catch (error) {
      throw new ApiError({
        code: 'CONTRACT_VIOLATION',
        message: `${route.operationId} emitted a chunk that is not JSON`,
        cause: error,
      });
    }
    return parseWith(route, route.response, chunk, validate);
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (value !== undefined) buffer += decoder.decode(value, { stream: true });
      if (done) break;
      for (;;) {
        const newline = buffer.indexOf('\n');
        if (newline < 0) break;
        const line = buffer.slice(0, newline).trim();
        buffer = buffer.slice(newline + 1);
        if (line.length > 0) yield emit(line);
      }
    }
    const tail = buffer.trim();
    if (tail.length > 0) yield emit(tail);
  } finally {
    // A consumer that breaks out of `for await` must not leave the socket open.
    await reader.cancel().catch(() => undefined);
  }
}

export const createApiClient = (options: ApiClientOptions = {}): ApiClient => {
  const config = resolveConfig(options);

  const operations: Record<string, unknown> = {
    routes: ROUTES,
    call: (route: AnyRoute, input?: RequestInputRecord, requestOptions?: RequestOptions) =>
      callRoute(route, input ?? {}, requestOptions, config),
    stream: (route: AnyRoute, input?: RequestInputRecord, requestOptions?: RequestOptions) =>
      streamRoute(route, input ?? {}, requestOptions, config),
  };

  for (const key of ROUTE_KEYS) {
    const route: AnyRoute = ROUTES[key];
    operations[key] =
      route.streaming === true
        ? (input?: RequestInputRecord, requestOptions?: RequestOptions) =>
            streamRoute(route, input ?? {}, requestOptions, config)
        : (input?: RequestInputRecord, requestOptions?: RequestOptions) =>
            callRoute(route, input ?? {}, requestOptions, config);
  }

  // The operations map is built by iterating the manifest, so its precise mapped
  // type cannot be expressed to the compiler; the manifest walk above is what
  // guarantees the shape, and `manifest.test.ts` asserts it stays exhaustive.
  return operations as unknown as ApiClient;
};
